/**
 * 腾讯广告命名模板页：策略配置×二级策略映射编辑器数据 Hook（CLAUDE.md §Hooks 规范）。
 *
 * 职责：
 *   - 拉取当前生效的映射记录（tencent_stra_second_mapping 单例）；
 *   - 从命名模板提取下拉选项：「策略配置」字段全部枚举 code；
 *     「二级策略」字段全部枚举 code（过滤 `*` / `%` 前缀的后缀项，如 *MTCL ——
 *     红线校验比对前会剔除该后缀，后缀项不参与映射配置）。
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../services/apiClient";
import { getLatestTemplate } from "../services/tencent/adNameTemplate";
import { getLatestMapping } from "../services/tencent/straSecondMapping";
import { pushToast } from "./useToast";
import type { AdNameTemplateRecord } from "../types/adNameTemplate.types";
import type { StraSecondMappingRecord } from "../types/straSecondMapping.types";

export interface StraSecondMappingData {
  loading: boolean;
  /** 当前生效的映射记录；无记录时为 null */
  record: StraSecondMappingRecord | null;
  /** 「策略配置」字段枚举 code 选项（保持模板配置顺序，去重） */
  straOptions: string[];
  /** 「二级策略」字段枚举 code 选项（已过滤 * / % 前缀后缀项） */
  secondOptions: string[];
  reload: () => Promise<void>;
}

export function useStraSecondMapping(): StraSecondMappingData {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<StraSecondMappingRecord | null>(null);
  const [straOptions, setStraOptions] = useState<string[]>([]);
  const [secondOptions, setSecondOptions] = useState<string[]>([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [template, mapping] = await Promise.all([
        getLatestTemplate(),
        getLatestMapping(),
      ]);
      setStraOptions(extractEnumCodes(template, "策略配置"));
      setSecondOptions(
        extractEnumCodes(template, "二级策略").filter(
          (code) => !code.startsWith("*") && !code.startsWith("%"),
        ),
      );
      setRecord(mapping);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "拉取映射配置失败，请稍后重试";
      pushToast(msg, "error");
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { loading, record, straOptions, secondOptions, reload };
}

/** 从命名模板指定字段的全部 SINGLE_ENUM 组合中提取去重后的 item_code 列表（保持配置顺序）。 */
function extractEnumCodes(
  template: AdNameTemplateRecord | null,
  fieldName: string,
): string[] {
  const field = (template?.fields ?? []).find((f) => f.name === fieldName);
  const codes: string[] = [];
  for (const combo of field?.combos ?? []) {
    if (combo.combo_type !== "SINGLE_ENUM") continue;
    for (const item of combo.single_enum_combo?.enum_items ?? []) {
      const code = item.item_code.trim();
      if (code && !codes.includes(code)) codes.push(code);
    }
  }
  return codes;
}
